import {JiraTicket} from 'views/home/dashboard/dashboardModel';

/** Dashboard Jira status keys returned by the home API. */
export type JiraStatusFilterKey = JiraTicket['status'];

export const JIRA_STATUS_FILTER_ORDER: JiraStatusFilterKey[] = [
  'not_started',
  'on_track',
  'at_risk',
  'complete',
];

/** Whether each status is shown when the viewer has no saved override. */
export const JIRA_STATUS_FILTER_DEFAULTS: Record<JiraStatusFilterKey, boolean> = {
  not_started: true,
  on_track: true,
  at_risk: true,
  complete: false,
};

export type JiraStatusFilterState = Partial<Record<JiraStatusFilterKey, boolean>>;

export const isJiraStatusEnabled = (
  filter: JiraStatusFilterState,
  status: JiraStatusFilterKey
): boolean => {
  if (status in filter) {
    return filter[status] === true;
  }
  return JIRA_STATUS_FILTER_DEFAULTS[status];
};

export const toggleJiraStatus = (
  filter: JiraStatusFilterState,
  status: JiraStatusFilterKey
): JiraStatusFilterState => {
  if (isJiraStatusEnabled(filter, status)) {
    return {...filter, [status]: false};
  }
  if (JIRA_STATUS_FILTER_DEFAULTS[status]) {
    const next = {...filter};
    delete next[status];
    return next;
  }
  return {...filter, [status]: true};
};

export const isJiraStatusFilterAtDefaults = (
  filter: JiraStatusFilterState,
  available: JiraStatusFilterKey[]
): boolean =>
  available.every(
    (status) => isJiraStatusEnabled(filter, status) === JIRA_STATUS_FILTER_DEFAULTS[status]
  );

/** Statuses present in the current ticket set, in display order. */
export const availableJiraStatuses = (tickets: JiraTicket[]): JiraStatusFilterKey[] => {
  const seen = new Set<JiraStatusFilterKey>();
  for (const ticket of tickets) {
    seen.add(ticket.status);
  }
  return JIRA_STATUS_FILTER_ORDER.filter((status) => seen.has(status));
};

export const filterJiraTicketsByStatus = (
  tickets: JiraTicket[],
  filter: JiraStatusFilterState,
  available: JiraStatusFilterKey[]
): JiraTicket[] => {
  const enabled = new Set(available.filter((status) => isJiraStatusEnabled(filter, status)));
  return tickets.filter((ticket) => enabled.has(ticket.status));
};
